import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../api';

function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [form, setForm] = useState({
    daily_send_cap: 50,
    send_delay_seconds: 60,
    public_base_url: '',
    google_places_api_key: '',
    auto_send_enabled: false,
    auto_send_campaign_id: '',
    auto_send_tag: 'cos',
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.get();
      const data = response.data?.data || {};
      setForm((prev) => ({
        ...prev,
        daily_send_cap: data.daily_send_cap ?? prev.daily_send_cap,
        send_delay_seconds: data.send_delay_seconds ?? prev.send_delay_seconds,
        public_base_url: data.public_base_url ?? prev.public_base_url,
        // google_places_api_key 出于安全考虑后端通常不回传，留空表示不修改
        google_places_api_key: data.google_places_api_key ?? prev.google_places_api_key,
        auto_send_enabled: !!data.auto_send_enabled,
        auto_send_campaign_id: data.auto_send_campaign_id || prev.auto_send_campaign_id,
        auto_send_tag: data.auto_send_tag ?? prev.auto_send_tag,
      }));
    } catch (error) {
      console.error('加载设置失败:', error);
      setMessage({ type: 'error', text: '加载设置失败：' + (error.response?.data?.message || error.message) });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const payload = {
        daily_send_cap: Number(form.daily_send_cap),
        send_delay_seconds: Number(form.send_delay_seconds),
        public_base_url: form.public_base_url,
        auto_send_enabled: form.auto_send_enabled,
        auto_send_tag: form.auto_send_tag,
      };
      if (form.google_places_api_key) payload.google_places_api_key = form.google_places_api_key;
      if (form.auto_send_campaign_id) payload.auto_send_campaign_id = form.auto_send_campaign_id;
      await settingsAPI.save(payload);
      setMessage({ type: 'success', text: '✅ 设置已保存！' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: '保存失败：' + (error.response?.data?.message || error.message) });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div>加载中...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>设置</h1>
        <p>配置发送节流、每日上限、退订域名及线索查找器密钥</p>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>系统设置</h2>
        </div>

        <div className="alert alert-info">
          为提升冷邮件送达率，请保持较低的每日发送上限并在每封之间留出间隔。
          过快或过量的群发容易触发邮件服务商的垃圾邮件检测，导致进入垃圾箱甚至封号。
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>默认每日发送上限（兜底值）</label>
            <input
              type="number"
              min="0"
              value={form.daily_send_cap}
              onChange={(e) => setForm({ ...form, daily_send_cap: e.target.value })}
              placeholder="例如: 50"
              required
            />
            <small style={{ color: '#888' }}>
              注意：每个发件邮箱有自己独立的每日上限（在「发件邮箱」里单独设置，按各自域名信誉暖机）；这里只是新邮箱的默认兜底值。
            </small>
          </div>

          <div className="form-group">
            <label>每封间隔秒数</label>
            <input
              type="number"
              min="0"
              value={form.send_delay_seconds}
              onChange={(e) => setForm({ ...form, send_delay_seconds: e.target.value })}
              placeholder="例如: 60"
              required
            />
          </div>

          <div className="form-group">
            <label>退订/公开域名</label>
            <input
              type="text"
              value={form.public_base_url}
              onChange={(e) => setForm({ ...form, public_base_url: e.target.value })}
              placeholder="例如: https://yx.serenejade.com"
            />
          </div>

          <div className="form-group">
            <label>Google Places API Key（线索查找器使用）</label>
            <input
              type="password"
              autoComplete="new-password"
              value={form.google_places_api_key}
              onChange={(e) => setForm({ ...form, google_places_api_key: e.target.value })}
              placeholder="用于线索查找器抓取公司信息"
            />
          </div>

          <div style={{ borderTop: '1px solid #eee', margin: '18px 0', paddingTop: '14px' }}>
            <h3 style={{ margin: '0 0 6px' }}>每日自动发送</h3>
            <div className="alert alert-info" style={{ marginTop: 0 }}>
              开启后，工作日（周一至周五）英国时间上午 9:30 自动发下一组（按上面的每日上限），
              只发指定标签里还没发过首封的人，发完整个名单自动停；回复/退订即停跟进。
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={form.auto_send_enabled}
                  onChange={(e) => setForm({ ...form, auto_send_enabled: e.target.checked })}
                  style={{ width: 'auto', marginRight: 8 }}
                />
                开启每日自动发送
              </label>
            </div>
            <div className="form-group">
              <label>自动发送的活动 ID</label>
              <input
                type="number"
                value={form.auto_send_campaign_id}
                onChange={(e) => setForm({ ...form, auto_send_campaign_id: e.target.value })}
                placeholder="例如 CoS 活动的 ID"
              />
            </div>
            <div className="form-group">
              <label>收件人标签</label>
              <input
                type="text"
                value={form.auto_send_tag}
                onChange={(e) => setForm({ ...form, auto_send_tag: e.target.value })}
                placeholder="例如 cos"
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '保存中...' : '保存设置'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Settings;
